import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Search, FileSpreadsheet, FileText, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API_URL, STATUS_STYLE, statusLabel, fmtUGX, downloadFeesExport } from './feesUtils';
import { StudentStatementDialog } from './StatementsTab';

const th = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap bg-muted sticky top-0 z-10 border-b';
const td = 'px-3 py-1.5 whitespace-nowrap border-b border-border/60 text-sm';
const tdNum = `${td} text-right tabular-nums`;

export const FeesTrackerTab = ({ config, school, year, term, refreshKey, refresh }) => {
  const [data, setData] = useState(null);
  const [classFilter, setClassFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('student_name');
  const [addOpen, setAddOpen] = useState(false);
  const [newStudent, setNewStudent] = useState({ name: '', class_name: '', registration_number: '' });
  const [statementStudent, setStatementStudent] = useState(null);
  const classes = config?.schools?.find((s) => s.key === school)?.classes || [];

  const load = useCallback(() => {
    const params = { school, academic_year: year, term };
    if (classFilter !== 'all') params.class_name = classFilter;
    axios.get(`${API_URL}/api/fees/tracker`, { params }).then((r) => setData(r.data)).catch(() => {});
  }, [school, year, term, classFilter]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setClassFilter('all'); }, [school]);

  const addStudent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/fees/students`, {
        name: newStudent.name, school, class_name: newStudent.class_name,
        registration_number: newStudent.registration_number || null,
      });
      toast.success('Student added to the directory');
      setAddOpen(false);
      setNewStudent({ name: '', class_name: '', registration_number: '' });
      load();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add student');
    }
  };

  let rows = data?.rows || [];
  if (search) rows = rows.filter((r) => (r.student_name || '').toLowerCase().includes(search.toLowerCase())
    || (r.registration_number || '').toLowerCase().includes(search.toLowerCase()));
  if (statusFilter !== 'all') {
    rows = statusFilter === 'with_arrears' ? rows.filter((r) => r.previous_arrears > 0)
      : statusFilter === 'with_balance' ? rows.filter((r) => r.current_balance > 0)
        : rows.filter((r) => r.payment_status === statusFilter);
  }
  rows = [...rows].sort((a, b) => sortKey === 'student_name'
    ? (a.student_name || '').localeCompare(b.student_name || '')
    : (b[sortKey] || 0) - (a[sortKey] || 0));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={classFilter} onValueChange={setClassFilter}>
          <SelectTrigger className="w-44" data-testid="tracker-class-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Classes</SelectItem>
            {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="tracker-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="paid_in_full">Paid in Full</SelectItem>
            <SelectItem value="partially_paid">Partially Paid</SelectItem>
            <SelectItem value="not_paid">Not Paid</SelectItem>
            <SelectItem value="with_arrears">With Arrears</SelectItem>
            <SelectItem value="with_balance">With Outstanding Balance</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortKey} onValueChange={setSortKey}>
          <SelectTrigger className="w-40" data-testid="tracker-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="student_name">Sort: Name</SelectItem>
            <SelectItem value="current_balance">Sort: Balance</SelectItem>
            <SelectItem value="total_paid">Sort: Total Paid</SelectItem>
            <SelectItem value="previous_arrears">Sort: Arrears</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8 w-56" placeholder="Search student…" value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="tracker-search" />
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadFeesExport('tracker',
            { school, academic_year: year, term, format: 'excel', ...(classFilter !== 'all' ? { class_name: classFilter } : {}) },
            `fees_tracker_${year}_T${term}.xlsx`)} data-testid="tracker-export-excel">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadFeesExport('tracker',
            { school, academic_year: year, term, format: 'pdf', ...(classFilter !== 'all' ? { class_name: classFilter } : {}) },
            `fees_tracker_${year}_T${term}.pdf`)} data-testid="tracker-export-pdf">
            <FileText className="h-3.5 w-3.5 mr-1" /> PDF
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="tracker-add-student-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Student
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-auto max-h-[560px]" data-testid="fees-tracker-table">
            <table className="w-full text-sm border-collapse min-w-[1250px]">
              <thead>
                <tr>
                  {['#', 'Student Name', 'Class', 'Reg. Fee', 'Fees Expected', '1st Inst.', '2nd Inst.', '3rd Inst.',
                    'Meals', 'Other', 'Total Paid', 'Prev. Arrears', 'Balance', 'Status', ''].map((h) => (
                      <th key={h} className={th}>{h}</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={15} className="text-center py-10 text-muted-foreground">
                    No students found — add students to this class or adjust filters
                  </td></tr>
                ) : rows.map((r, i) => (
                  <tr key={r.student_id} className="hover:bg-muted/40 cursor-pointer" data-testid={`tracker-row-${r.student_id}`}
                    onClick={() => setStatementStudent(r.student_id)}>
                    <td className={td}>{i + 1}</td>
                    <td className={`${td} font-medium`}>{r.student_name}</td>
                    <td className={td}>{r.class_name}</td>
                    <td className={tdNum}>{Number(r.registration_paid).toLocaleString()}</td>
                    <td className={`${tdNum} font-medium`}>{Number(r.expected_fees).toLocaleString()}</td>
                    <td className={tdNum}>{Number(r.first_installment).toLocaleString()}</td>
                    <td className={tdNum}>{Number(r.second_installment).toLocaleString()}</td>
                    <td className={tdNum}>{Number(r.third_installment).toLocaleString()}</td>
                    <td className={tdNum}>{Number(r.meals_paid).toLocaleString()}</td>
                    <td className={tdNum}>{Number(r.other_paid + r.school_fees_paid).toLocaleString()}</td>
                    <td className={`${tdNum} font-semibold text-green-700`}>{Number(r.total_paid).toLocaleString()}</td>
                    <td className={`${tdNum} text-orange-600`}>{Number(r.previous_arrears).toLocaleString()}</td>
                    <td className={`${tdNum} font-semibold ${r.current_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{Number(r.current_balance).toLocaleString()}</td>
                    <td className={td}><Badge className={`capitalize ${STATUS_STYLE[r.payment_status] || ''}`} variant="outline">{statusLabel(r.payment_status)}</Badge></td>
                    <td className={td}><Eye className="h-3.5 w-3.5 text-muted-foreground" /></td>
                  </tr>
                ))}
              </tbody>
              {data && rows.length > 0 && (
                <tfoot>
                  <tr className="bg-muted font-bold sticky bottom-0">
                    <td className={td} colSpan={3}>TOTALS ({rows.length} students)</td>
                    <td className={tdNum}>{Number(data.totals.registration_paid).toLocaleString()}</td>
                    <td className={tdNum}>{Number(data.totals.expected_fees).toLocaleString()}</td>
                    <td className={tdNum}>{Number(data.totals.first_installment).toLocaleString()}</td>
                    <td className={tdNum}>{Number(data.totals.second_installment).toLocaleString()}</td>
                    <td className={tdNum}>{Number(data.totals.third_installment).toLocaleString()}</td>
                    <td className={tdNum}>{Number(data.totals.meals_paid).toLocaleString()}</td>
                    <td className={tdNum}>{Number(data.totals.other_paid + data.totals.school_fees_paid).toLocaleString()}</td>
                    <td className={`${tdNum} text-green-700`}>{Number(data.totals.total_paid).toLocaleString()}</td>
                    <td className={`${tdNum} text-orange-600`}>{Number(data.totals.previous_arrears).toLocaleString()}</td>
                    <td className={`${tdNum} text-red-600`}>{Number(data.totals.current_balance).toLocaleString()}</td>
                    <td className={td} colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add student */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Student</DialogTitle>
            <DialogDescription>Added to the shared Student Directory — no duplicates allowed</DialogDescription>
          </DialogHeader>
          <form onSubmit={addStudent} className="space-y-3">
            <div className="space-y-1.5"><Label>Full Name *</Label>
              <Input required value={newStudent.name} onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })} data-testid="add-student-name" /></div>
            <div className="space-y-1.5"><Label>Class *</Label>
              <Select value={newStudent.class_name} onValueChange={(v) => setNewStudent({ ...newStudent, class_name: v })}>
                <SelectTrigger data-testid="add-student-class"><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent>{classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Registration Number</Label>
              <Input value={newStudent.registration_number} onChange={(e) => setNewStudent({ ...newStudent, registration_number: e.target.value })} data-testid="add-student-regno" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!newStudent.name || !newStudent.class_name} data-testid="add-student-submit">Add Student</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <StudentStatementDialog studentId={statementStudent} year={year} term={term}
        onClose={() => setStatementStudent(null)} />
    </div>
  );
};
